import { useMemo } from 'react';

/**
 * Interface for the mapping configuration.
 */
interface SelectionMapping {
  labelKey: string;
  valueKey: string;
  dataKey?: string; // Key where the array resides (e.g., 'grades' or 'data')
  params?: any; // Query parameters for the API call
  skip?: boolean; // Condition to skip the API call
  transformLabel?: (item: any) => string; // Optional label formatter
}

/**
 * A highly reusable hook to fetch and transform API data into Ant Design Select options.
 * It encapsulates the RTK Query call, loading state, and data mapping logic.
 *
 * @param queryHook - The RTK Query hook function (e.g., useGetAllDepartmentsQuery)
 * @param config - Mapping and behavioral configuration
 */
export default function useSelectionOptions(
  queryHook: any,
  config: SelectionMapping,
) {
  // Execute the passed RTK Query hook inside this hook
  // It only triggers the specific API passed as 'queryHook'
  const { data, isLoading, isError } = queryHook(config.params || {}, {
    skip: config.skip,
  });

  // Destructure config to satisfy React Hook dependencies
  const { dataKey, labelKey, valueKey, transformLabel } = config;

  const options = useMemo(() => {
    if (!data) return [];

    // HRX Standard: Data payload is usually in 'data.data'
    let source = data?.data;

    // Navigate to the specific array if a secondary key is provided
    if (dataKey && source?.[dataKey]) {
      source = source[dataKey];
    } else if (source?.data && Array.isArray(source.data)) {
      // Handle data.data.data structure commonly found in paginated results
      source = source.data;
    }

    if (!Array.isArray(source)) return [];

    return source.map((item: any) => ({
      label: transformLabel ? transformLabel(item) : item[labelKey],
      value: item[valueKey],
    }));
    // Including specific keys in dependency array to ensure stability
  }, [data, dataKey, labelKey, valueKey, transformLabel]);

  return [options, isLoading, isError, data];
}

